"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  getServiceWorkerRegistration,
  SERVICE_WORKER_SKIP_WAITING_MESSAGE,
} from "@/lib/pwa/service-worker-client";

const POLL_MS = 400;
const POLL_MAX_MS = 12000;

type PWAUpdatePromptProps = {
  withBottomNavOffset?: boolean;
};

/**
 * Shows when a new service worker is installed but waiting (`skipWaiting` is user-driven via message).
 * User taps Refresh to activate and reload.
 */
export default function PWAUpdatePrompt({
  withBottomNavOffset = false,
}: PWAUpdatePromptProps) {
  const [visible, setVisible] = useState(false);
  const reloadOnControllerChangeRef = useRef(false);
  /** User chose "Later" for the current waiting worker; reset on `updatefound`. */
  const userDismissedRef = useRef(false);
  const listenersAttachedRef = useRef(false);

  const syncWaitingVisibility = useCallback(
    (reg: ServiceWorkerRegistration) => {
      const hasActiveController = Boolean(navigator.serviceWorker.controller);
      const waiting = reg.waiting;
      if (hasActiveController && waiting) {
        if (!userDismissedRef.current) {
          setVisible(true);
        }
      } else if (!waiting) {
        setVisible(false);
      }
    },
    [],
  );

  const onRefresh = useCallback(() => {
    if (!("serviceWorker" in navigator)) return;
    void getServiceWorkerRegistration().then((reg) => {
      const waiting = reg?.waiting;
      if (!waiting) return;
      reloadOnControllerChangeRef.current = true;
      waiting.postMessage(SERVICE_WORKER_SKIP_WAITING_MESSAGE);
    });
  }, []);

  const onDismiss = useCallback(() => {
    userDismissedRef.current = true;
    setVisible(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    const onControllerChange = () => {
      if (reloadOnControllerChangeRef.current) {
        window.location.reload();
      }
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    let cancelled = false;
    /** Browser timer handle (`number`); avoid `NodeJS.Timeout` from merged typings. */
    let pollId: number | null = null;

    const attachToRegistration = (reg: ServiceWorkerRegistration) => {
      if (listenersAttachedRef.current) {
        syncWaitingVisibility(reg);
        return;
      }
      listenersAttachedRef.current = true;

      syncWaitingVisibility(reg);

      reg.addEventListener("updatefound", () => {
        userDismissedRef.current = false;
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (cancelled) return;
          if (
            installing.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            syncWaitingVisibility(reg);
          }
        });
      });
    };

    const tryAttach = async () => {
      const reg = await getServiceWorkerRegistration();
      if (cancelled || !reg) return;
      attachToRegistration(reg);
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    void tryAttach();
    pollId = window.setInterval(() => void tryAttach(), POLL_MS);
    const clearPollTimeout = window.setTimeout(() => {
      if (pollId !== null) {
        clearInterval(pollId);
        pollId = null;
      }
    }, POLL_MAX_MS);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
      if (pollId !== null) {
        clearInterval(pollId);
      }
      clearTimeout(clearPollTimeout);
    };
  }, [syncWaitingVisibility]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed left-4 right-4 bg-surface rounded-lg shadow-lg p-4 mx-auto max-w-sm z-[60] border border-border ${
        withBottomNavOffset
          ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
          : "bottom-4"
      }`}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss update notice"
        className="absolute top-2 right-2 text-text-light hover:text-foreground"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
      <p className="text-sm font-medium text-foreground pr-8 mb-3">
        Update available — refresh to get the latest version.
      </p>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-text-light hover:text-foreground"
        >
          Later
        </button>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          <ArrowPathIcon className="h-4 w-4 shrink-0" aria-hidden />
          Refresh
        </button>
      </div>
    </div>
  );
}
